/**
 * AuthHub — unified login / signup screen orchestrator.
 *
 * Split into topic panels in 2026-02 (part of the "keep React components
 * under 200 lines" refactor). This file is now just the mode-state machine;
 * every visual concern lives in ./auth/<Panel>.jsx.
 *
 * Modes:
 *   • "login"          → email + password
 *   • "consent"        → Terms + Privacy scroll-to-end gate
 *   • "pick-role"      → choose Lawyer vs Visitor
 *   • "signup-lawyer"  → scan bar card → name + email + 2× password
 *   • "signup-client"  → name + email + 2× password
 *   • "otp"            → 6-digit OTP verify (resend with 60s cooldown)
 *   • "forgot-email"   → request reset code
 *   • "reset-password" → OTP + new password
 */
import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useUser } from "../context/UserAuthContext";
import { useAdmin } from "../context/AdminAuthContext";
import { ScreenShell, GhostBackButton } from "./auth/_shared";
import LoginPanel from "./auth/LoginPanel";
import ConsentPanel from "./auth/ConsentPanel";
import RolePickerPanel from "./auth/RolePickerPanel";
import LawyerSignupPanel from "./auth/LawyerSignupPanel";
import ClientSignupPanel from "./auth/ClientSignupPanel";
import ForgotEmailPanel from "./auth/ForgotEmailPanel";
import ResetPasswordPanel from "./auth/ResetPasswordPanel";
import OtpPanel from "./auth/OtpPanel";

export default function AuthHub({ initialMode = "login", mode: modeProp }) {
  const { user, loading, refresh: refreshUser } = useUser();
  const { refresh: refreshAdmin } = useAdmin();
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from?.pathname || "/";

  // Support the legacy `mode` prop still passed from App.js's /signup and
  // /login routes. Map `mode="signup"` → "pick-role" (the actual first
  // signup screen), else fall through to `initialMode` for direct usage.
  const resolvedInitial =
    modeProp === "signup" ? "pick-role" :
    modeProp === "login" ? "login" :
    initialMode;
  const [mode, setMode] = useState(resolvedInitial);
  const [pending, setPending] = useState(null); // {email, role, name}

  if (loading) return null;
  if (user) return <Navigate to={from} replace />;

  const handleAuthed = async ({ admin } = {}) => {
    await refreshUser();
    await refreshAdmin();
    toast.success(admin ? "أهلاً بك يا مدير النظام" : "أهلاً بك في LexIQ");
    nav(admin ? "/admin" : from, { replace: true });
  };

  return mode === "consent" && pending ? (
    <ConsentPanel
      role={pending.role}
      onBack={() => setMode("pick-role")}
      onAgreed={() => setMode(pending.role === "client" ? "signup-client" : "signup-lawyer")}
    />
  ) : (
    <ScreenShell>
      {mode === "login" && (
        <LoginPanel
          onSwitchSignup={() => setMode("pick-role")}
          onForgotPassword={() => setMode("forgot-email")}
          onAuthed={handleAuthed}
        />
      )}
      {mode === "forgot-email" && (
        <ForgotEmailPanel
          onBack={() => setMode("login")}
          onSent={(p) => { setPending(p); setMode("reset-password"); }}
        />
      )}
      {mode === "reset-password" && pending && (
        <ResetPasswordPanel
          pending={pending}
          onBack={() => setMode("forgot-email")}
          onResetDone={handleAuthed}
        />
      )}
      {mode === "pick-role" && (
        <>
          <GhostBackButton onClick={() => setMode("login")} testid="back-to-login" />
          <RolePickerPanel
            onPickLawyer={() => { setPending({ role: "lawyer" }); setMode("consent"); }}
            onPickClient={() => { setPending({ role: "client" }); setMode("consent"); }}
          />
        </>
      )}
      {mode === "signup-lawyer" && (
        <LawyerSignupPanel
          onBack={() => setMode("consent")}
          onPending={(p) => { setPending(p); setMode("otp"); }}
        />
      )}
      {mode === "signup-client" && (
        <ClientSignupPanel
          onBack={() => setMode("consent")}
          onPending={(p) => { setPending(p); setMode("otp"); }}
        />
      )}
      {mode === "otp" && pending && (
        <OtpPanel
          pending={pending}
          onBack={() => setMode(pending.role === "client" ? "signup-client" : "signup-lawyer")}
          onVerified={handleAuthed}
        />
      )}
    </ScreenShell>
  );
}

